import useIsMobile from 'utils/hooks/useIsMobile'
import {Autocomplete, Button, FormHelperText, TextField} from '@mui/material'
import fileDownload from 'js-file-download'
import _ from 'lodash'
import {useEffect, useState} from 'react'
import {useDropzone} from 'react-dropzone'
import {useSelector} from 'react-redux'
import {toast, ToastContainer} from 'react-toastify'
import {envBackHost} from '../../../GLOBAL/envVariables'
import {axiosWithAuth} from '../../../helper/axiosWithAuth'
import {normalAlert, resultAlert} from '../../../utils/CustomAlert/Alert'
import useOfficeHierarchy from 'utils/hooks/useOfficeHierarchy'
import './../scrollbar.css'

const BatchRegisterOffcanvas = () => {
  const isMobile = useIsMobile()
  const userInfo = useSelector((state: any) => state.userInfo?.userInfo)
  const [file, setFile] = useState<File>()
  const [fileName, setFileName] = useState<string>('')
  const [processing, setProcessing] = useState<boolean>(false)
  const [failList, setFailList] = useState<any[]>([])
  const [officeList, setOfficeList] = useState<any[]>([])
  const officeHierarchy = useOfficeHierarchy(officeList)
  const [batchInfo, setBatchInfo] = useState<any>({
    officeId: '',
    userId: '',
    officeLabel: '',
  })
  const [selectedOffice, setSelectedOffice] = useState<any>(null)

  const {getRootProps, getInputProps, isDragActive} = useDropzone({
    multiple: false,
    accept: {'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx']},
    onDrop: (acceptedFiles) => {
      if (acceptedFiles.length > 0) {
        const dropped = acceptedFiles[0]
        setFile(dropped)
        setFileName(dropped.name)
      }
    },
  })

  const exampleExcelFile = async () => {
    if (!batchInfo?.officeId || batchInfo?.officeId === '') {
      await normalAlert('사업소를 선택해주세요.')
      return
    }
    axiosWithAuth()
      .get(`${envBackHost}/${batchInfo?.officeId}/customer/example`, {
        responseType: 'blob',
      })
      .then(async (res) => {
        await resultAlert(
          {resultCode: 0},
          `[ ${batchInfo?.officeLabel} ] 사업소 샘플엑셀양식이 다운로드 완료되었습니다.`
        )
        fileDownload(res.data, `${batchInfo?.officeLabel}_excel_example_download.xlsx`)
      })
      .catch(async (e) => {
        await resultAlert({resultCode: e.response.status})
      })
  }

  const getOfficeList = async () => {
    await axiosWithAuth()
      .get(`${envBackHost}/office/${userInfo?.officeId}`)
      .then((res) => {
        const transformed = res.data.res.map((el: any) => ({
          label: el.officeName,
          value: el.officeId,
        }))
        setOfficeList(transformed)
      })
  }

  const fileUploadHandler = async () => {
    const formData = new FormData()
    formData.append('file', file as Blob)
    formData.append('id', batchInfo?.userId)
    formData.append('officeId', batchInfo?.officeId)
    setProcessing(true)

    await toast.promise(
      axiosWithAuth()
        .post(`${envBackHost}/customer/excel`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        })
        .then(async (res) => {
          console.log('res', res.data)
          setProcessing(false)
          if (res.data.resultCode === 0) {
            const closeBtn = document.querySelector('#batchRegister .btn-close') as HTMLElement
            closeBtn?.click()
            setFileName('')
            setFile(undefined)
            setSelectedOffice(null)
            setBatchInfo({...batchInfo, officeId: '', officeLabel: ''})
            setProcessing(false)
            return await resultAlert(res.data)
          } else if (res.data.resultCode === 30) {
            setFailList(res?.data?.res)
            setProcessing(false)
            return await resultAlert(res.data.resultCode, res?.data?.message)
          }
        })
        .catch(async (e) => {
          await resultAlert({resultCode: e.response.status})
          setProcessing(false)
        })
        .finally(() => {
          setProcessing(false)
        }),
      {pending: '수용가 일괄 등록 중 입니다.'},
      {
        position: 'top-center',
        closeButton: false,
        className: 'fs-3  fw-bold',
        style: {width: '400px'},
      }
    )
  }

  useEffect(() => {
    setFailList([])
  }, [fileName])

  useEffect(() => {
    getOfficeList()
    setBatchInfo({
      officeId: '',
      userId: userInfo?.userId,
    })
  }, [])

  return (
    <>
      <div
        className='offcanvas offcanvas-end'
        tabIndex={-1}
        id='batchRegister'
        aria-labelledby='batchRegisterLabel'
        style={{width: isMobile ? '100%' : '40%'}}
      >
        <div className='offcanvas-header'>
          <span className='font-semibold text-[1.5rem]'>수용가 일괄등록</span>
          <button
            type='button'
            className='btn-close text-reset'
            data-bs-dismiss='offcanvas'
            aria-label='Close'
          />
        </div>
        <div className='offcanvas-body batch-scrollbar'>
          <div className={'row gap-4'}>
            <div className={`d-flex flex-column gap-3 w-100 px-0 mx-0`}>
              {/* 드래그앤드롭 파일 업로드 영역 */}
              <div style={{position: 'relative'}}>
                <div
                  {...getRootProps()}
                  className={[
                    'w-100 border-2 border-dashed rounded-lg d-flex flex-column align-items-center justify-content-center cursor-pointer transition-colors',
                    isDragActive ? 'border-primary bg-light-primary' : 'border-secondary',
                  ].join(' ')}
                  style={{minHeight: '100px', padding: '16px'}}
                >
                  <input {...getInputProps()} />
                  {fileName ? (
                    <span className='fw-bold fs-5 text-primary text-center'>{fileName}</span>
                  ) : (
                    <>
                      <span className='fw-bold fs-5 text-muted'>
                        {isDragActive ? '파일을 놓아주세요!' : '파일을 드래그하거나 클릭하여 선택'}
                      </span>
                      <span className='text-muted fs-7 mt-1'>xlsx 파일만 업로드 가능합니다</span>
                    </>
                  )}
                </div>
                {fileName && (
                  <button
                    type='button'
                    onClick={(e) => {
                      e.stopPropagation()
                      setFile(undefined)
                      setFileName('')
                    }}
                    style={{
                      position: 'absolute',
                      top: '6px',
                      right: '8px',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: '18px',
                      color: '#999',
                      lineHeight: 1,
                    }}
                    title='파일 제거'
                  >
                    ✕
                  </button>
                )}
              </div>
              <Button
                variant={'contained'}
                color={'success'}
                onClick={() => exampleExcelFile()}
                fullWidth
              >
                샘플양식 다운로드
              </Button>
            </div>
            <FormHelperText className='fs-6 fw-bold text-danger mb-1'>
              {fileName === '' ? (
                '파일을 선택해주세요.'
              ) : failList?.length > 0 ? (
                <span className='text-primary'>
                  잘못 업로드한 경우 다시 찾아보기를 클릭해주세요.
                </span>
              ) : (
                <span className='text-primary'>사업소를 선택하고 일괄등록을 눌러주세요.</span>
              )}
            </FormHelperText>

            {/* 사업소 선택 — AsideToolbar 방식 계층형 */}
            <Autocomplete
              disablePortal
              fullWidth
              id='choose-office'
              options={officeHierarchy.options}
              className={'px-0'}
              clearText={'초기화'}
              noOptionsText={'검색 결과가 없습니다.'}
              getOptionLabel={(option: any) => option.label ?? ''}
              filterOptions={officeHierarchy.filterOptions}
              value={selectedOffice}
              renderOption={officeHierarchy.renderOption}
              onChange={(_e, rowValue: any) => {
                setSelectedOffice(rowValue)
                setBatchInfo({
                  ...batchInfo,
                  officeId: rowValue?.value ?? '',
                  officeLabel: rowValue?.label ?? '',
                })
                if (isMobile) {
                  ;(document.activeElement as HTMLElement)?.blur()
                }
              }}
              sx={{fontSize: '1.5rem'}}
              renderInput={(params) => (
                <div className={'d-flex gap-3'}>
                  <TextField
                    {...params}
                    fullWidth
                    label='사업소 선택'
                    placeholder={'검색하거나 선택할 수 있습니다.'}
                  />
                </div>
              )}
            />
          </div>

          {failList?.length > 0 && (
            <>
              <div className={'my-4 d-flex px-2'}>
                <h3 className={'text-danger fs-2'}> {fileName} 업로드 실패 목록</h3>
                <span className={'text-muted fw-bolder mx-2 fs-5'}>( {failList?.length}건 )</span>
              </div>
              <table
                className='table table-hover table-rounded border gy-3 gs-3 text-center'
                style={{overflow: 'scroll', maxHeight: '1024px'}}
              >
                <thead style={{position: 'sticky', top: 0}} className={'bg-gray-600'}>
                  <tr className='fw-bolder fs-5 border-bottom-2 border-gray-200 text-white'>
                    <th>행 번호</th>
                    <th>수용가 번호</th>
                    <th>수용가 이름</th>
                    <th>실패 사유</th>
                  </tr>
                </thead>
                <tbody>
                  {_.map(failList, (el, i) => (
                    <tr key={i}>
                      <td>{el?.excelRowIndex}</td>
                      <td>{el?.customerNumber}</td>
                      <td>{el?.customerName}</td>
                      <td className='text-left'>
                        {el?.messages.length === 1 ? el?.messages : `확인필요 (${el?.messages})`}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </div>
        <div className={'offcanvas-footer'}>
          <div
            className={'d-flex gap-7 justify-content-end'}
            style={{marginBottom: '50px', marginRight: '30px'}}
          >
            <Button
              variant={'contained'}
              size={'large'}
              color={'info'}
              onClick={() => fileUploadHandler()}
              disabled={batchInfo?.officeId === '' || processing}
            >
              일괄등록
            </Button>
            <Button
              id={'batchRegisterCustomerClose'}
              variant={'contained'}
              size={'large'}
              color={'error'}
              data-bs-dismiss='offcanvas'
              disabled={processing}
              onClick={() => {
                setBatchInfo({
                  officeId: '',
                  userId: userInfo?.userId ?? '',
                  officeLabel: '',
                })
                setSelectedOffice(null)
                setFile(undefined)
                setFileName('')
                setFailList([])
              }}
            >
              나가기
            </Button>
          </div>
        </div>
      </div>
      <ToastContainer />
    </>
  )
}
export default BatchRegisterOffcanvas
